import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { HiOutlineChevronDown } from "react-icons/hi";
import Typography from "@mui/material/Typography";
import { Container } from "react-bootstrap";
import moment from "moment";
import { AccordionRequest, Content, Bold, Title, PageWrap } from "./styles";
import ProductCard from "./product-card";
import ProductHotel from "./product-hotel";
import ProductHeader from "./product-header";
import ProductAereo from "./product-aereo";
import ProductTransfer from "./product-transfer";
import { pedidosApi } from "page/OrderResumePag/_services/api";
import { useUserData } from "../../hooks/UserContext";
import { LoaderSmall } from "components/Loader";
import { trackClick } from "../../utils/gtm";
import { tagIdFromLegacySuffix } from "../../utils/marketplaceTagIds";
import { useScreenTracking } from "../../hooks/useScreenTracking";

export default function MyRequests() {
  const location = useLocation();
  const screenName = useScreenTracking();
  const [dataResponse, setDataResponse] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [period, setPeriod] = useState(30);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const { user } = useUserData();
  const customerUuid = user?.uuid;
  const limit = 10;
  const loadMoreRef = useRef(null);

  const periods = [
    { value: 30, label: "Últimos 30 dias" },
    { value: 60, label: "Últimos 60 dias" },
    { value: 90, label: "Últimos 90 dias" },
  ];

  const getPeriodAnalyticsTitle = (periodValue) => {
    const periodMap = {
      30: "periodo-ultimos-30-dias",
      60: "periodo-ultimos-60-dias",
      90: "periodo-ultimos-90-dias",
    };
    return periodMap[periodValue];
  };

  const sendMyRequestsAnalytics = (customTitle) => {
    trackClick({
      title: customTitle,
      contentType: "meus-pedidos",
      screenName,
      tagId: tagIdFromLegacySuffix("10"),
    });
  };

  const getFirstNumbersOrder = (orderUuid) => {
    return orderUuid.substring(0, 8).toUpperCase();
  };

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getPeriod = (days = period) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    return { startDate: formatDate(start), endDate: formatDate(end) };
  };

  const handleChangeRange = (event) => {
    const value = event.target.value;

    sendMyRequestsAnalytics(getPeriodAnalyticsTitle(value));

    setPeriod(value);
    setDataResponse([]);
    setPage(1);
    setHasMore(true);
  };

  const getLocatorAsString = (locator) => {
    if (!locator) return "";
    if (typeof locator === "string") return locator;
    if (typeof locator === "object") {
      return (
        locator.code ||
        locator.value ||
        locator.locator ||
        JSON.stringify(locator)
      );
    }
    return String(locator);
  };

  const getOrderStatus = (order) => {
    const status = order.status.toLowerCase();
    return status;
  };

  const handleAccordionChange = (orderIndex, order) => (event, isExpanded) => {
    const orderKey = `${order.orderUuid}-${orderIndex}`;
    const orderStatus = getOrderStatus(order);

    if (isExpanded) {
      setExpandedOrders((prev) => new Set(prev.add(orderKey)));

      sendMyRequestsAnalytics(`abriu-pedido-${orderStatus}`);
    } else {
      setExpandedOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(orderKey);
        return newSet;
      });

      sendMyRequestsAnalytics(`fechou-pedido-${orderStatus}`);
    }
  };

  const safeString = (value, fallback = "") => {
    if (!value && value !== 0) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (typeof value === "object") {
      return value.code || value.value || value.name || String(value);
    }
    return String(value);
  };

  // Backend de passagens/hotéis retorna orderDate sem marcador de fuso (ex: "2026-04-16 00:23:53"),
  // mas o valor está em UTC. Sem esse tratamento o moment interpreta como horário local do browser
  // e pedidos feitos após 21h BRT aparecem 1 dia à frente.
  const formatOrderDate = (rawOrder) => {
    if (!rawOrder) return "";
    if (rawOrder.orderDateFormattedBr) {
      return rawOrder.orderDateFormattedBr.split(" ")[0];
    }
    if (!rawOrder.orderDate) return "";
    const hasTzMarker = /(Z|[+-]\d{2}:?\d{2})$/.test(rawOrder.orderDate);
    if (hasTzMarker) {
      return moment(rawOrder.orderDate).format("DD/MM/YYYY");
    }

    const dateUtc = moment.utc(rawOrder.orderDate).toDate();
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
    }).format(dateUtc);
  };

  // Regex patterns para validar os status da API
  const statusPatterns = {
    recebido: /recebido|aprovado/i,
    confirmado: /confirmado|aprovado|/i,
    faturado: /faturado|nota\s*fiscal/i,
    enviado: /enviado|despachado|em\s*tr[aâ]nsito/i,
    entregue: /entregue|finalizado/i,
  };

  // Função para verificar se um status da API corresponde a um step padrão
  const matchStatusToStep = (apiStatus, stepKey) => {
    if (!apiStatus || !stepKey) return false;
    const pattern = statusPatterns[stepKey];
    return pattern ? pattern.test(apiStatus) : false;
  };

  const mapProductData = (order) => {
    if (!order.brands) return [];

    return Object.values(order.brands).map((brand) => {
      const totalProductPrice =
        brand.products?.reduce((sum, product) => {
          return sum + parseFloat(product.priceTotal || product.price || 0);
        }, 0) || 0;

      // Definir os 5 steps padrão da régua com suas keys para matching
      const defaultSteps = [
        { label: "Pedido recebido", key: "recebido" },
        { label: "Pedido confirmado", key: "confirmado" },
        { label: "Pedido faturado", key: "faturado" },
        { label: "Pedido enviado", key: "enviado" },
        { label: "Pedido entregue", key: "entregue" },
      ];

      let trackingSteps = [];

      const apiTrackingMap = {};
      const hasTracking = !brand.manual;

      if (hasTracking) {
        brand.tracking.forEach((track) => {
          // Validar cada status da API e mapear para o step correspondente
          defaultSteps.forEach((step) => {
            if (matchStatusToStep(track.status, step.key)) {
              // Usar a primeira ocorrência encontrada (não sobrescrever)
              if (!apiTrackingMap[step.key]) {
                apiTrackingMap[step.key] = track.actionDate;
              }
            }
          });
        });
      }

      trackingSteps = defaultSteps.map((step, index) => {
        const actionDate = apiTrackingMap[step.key];

        if (index === 0 || index === 1) {
          return {
            label: step.label,
            date: actionDate
              ? moment(actionDate).format("DD/MM/YYYY")
              : formatOrderDate(order),
            completed: true,
          };
        }

        return {
          label: step.label,
          date: actionDate ? moment(actionDate).format("DD/MM/YYYY") : "",
          completed: !!actionDate,
        };
      });

      return {
        brand: brand.brandName,
        name: brand.products?.map((p) => p.product).join(", ") || "",
        discount: parseFloat(order.discount || 0),
        price: totalProductPrice,
        shipping: parseFloat(brand.shipping || 0),
        shippingPoints: parseFloat(brand["shipping-points"] || 0),
        deliveryDate: brand.deliveryDate
          ? moment(brand.deliveryDate).format("DD/MM/YYYY")
          : "",
        status: trackingSteps,
        hasTracking: hasTracking,
        products: brand.products || [],
      };
    });
  };

  const mapHotelData = (order) => {
    const hotel = order.hotel;
    const travellers = order.travellers || [];

    if (!hotel) return null;

    const checkInDate = moment(hotel.checkIn);
    const checkOutDate = moment(hotel.checkOut);
    const nights = checkOutDate.diff(checkInDate, "days");

    return {
      guestName: travellers
        .map((t) => `${safeString(t.firstName)} ${safeString(t.lastName)}`)
        .join(", "),
      locator: getLocatorAsString(order.recordLocator) || "N/A",
      destination: safeString(hotel.address?.city),
      hotelName: safeString(hotel.name),
      checkIn: checkInDate.format("DD/MM/YYYY"),
      checkOut: checkOutDate.format("DD/MM/YYYY"),
      nights: nights,
      guests: travellers.length,
      board: hotel.room.board,
      roomName: hotel.room.name,
    };
  };

  const mapAereoData = (order) => {
    const segments = order.segments || [];
    const passengers = order.passengers || [];
    const passengerNames = passengers
      .map((p) => `${safeString(p.firstName)} ${safeString(p.lastName)}`)
      .join(", ")
      .toUpperCase();

    return {
      title: "Passagem",
      legs: segments.map((segment, segIdx) => {
        const flights = segment.flights || [];
        const mainFlight = flights[0] || {};
        const lastFlight = flights[flights.length - 1] || mainFlight;

        let layover = "";
        if (segIdx > 0 && segments[segIdx - 1]) {
          const prevSegment = segments[segIdx - 1];
          const prevLastFlight =
            prevSegment.flights?.[prevSegment.flights.length - 1];
          if (prevLastFlight && mainFlight.departureDateTime) {
            const prevArrival = moment(prevLastFlight.arrivalDateTime);
            const currentDeparture = moment(mainFlight.departureDateTime);
            const diffMinutes = currentDeparture.diff(prevArrival, "minutes");
            const hours = Math.floor(diffMinutes / 60);
            const minutes = diffMinutes % 60;
            layover = `${String(hours).padStart(2, "0")}h${String(minutes).padStart(2, "0")}min em ${prevLastFlight.destination}`;
          }
        }

        return {
          label: segment.type === "ida" ? "Ida" : "Volta",
          airline: safeString(
            order.airline || mainFlight.carrier,
          ).toUpperCase(),
          locator: getLocatorAsString(order.recordLocator),
          flightNumber: safeString(mainFlight.flightNumber),
          passengers: passengerNames,
          origin: safeString(mainFlight.origin),
          destination: safeString(lastFlight.destination),
          departure: mainFlight.departureDateTime
            ? moment(mainFlight.departureDateTime).format(
                "DD/MM/YYYY [às] HH:mm",
              )
            : "",
          arrival: lastFlight.arrivalDateTime
            ? moment(lastFlight.arrivalDateTime).format("DD/MM/YYYY [às] HH:mm")
            : "",
          layover: layover,
          stops: flights.slice(1).map((flight, idx) => {
            let waitTime = "";
            if (idx < flights.length - 1) {
              const currentArrival = moment(flight.arrivalDateTime);
              const nextFlight = flights[idx + 1];
              if (nextFlight) {
                const nextDeparture = moment(nextFlight.departureDateTime);
                const diffMinutes = nextDeparture.diff(
                  currentArrival,
                  "minutes",
                );
                const hours = Math.floor(diffMinutes / 60);
                const minutes = diffMinutes % 60;
                waitTime = `${String(hours).padStart(2, "0")}h${String(minutes).padStart(2, "0")}min em ${flight.destination}`;
              }
            }

            return {
              airline: safeString(flight.carrier).toUpperCase(),
              locator: getLocatorAsString(order.recordLocator),
              flightNumber: safeString(flight.flightNumber),
              passengers: passengerNames,
              origin: `${safeString(flight.origin)} - ${safeString(flight.origin)}`,
              destination: `${safeString(flight.destination)} - ${safeString(flight.destination)}`,
              departure: flight.departureDateTime
                ? moment(flight.departureDateTime).format(
                    "DD/MM/YYYY [às] HH:mm",
                  )
                : "",
              arrival: flight.arrivalDateTime
                ? moment(flight.arrivalDateTime).format("DD/MM/YYYY [às] HH:mm")
                : "",
              waitTime: waitTime,
            };
          }),
        };
      }),
    };
  };

  const mapPaymentData = (order) => {
    const paymentType = order.payment_type || order.payment?.type;
    const payPriceCents =
      typeof order.pay_price === "number" ? order.pay_price : 0;
    const payPoints =
      typeof order.pay_points === "number" ? order.pay_points : 0;
    const priceBRL = payPriceCents > 0 ? payPriceCents / 100 : 0;

    const formatBRL = (value) =>
      `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatPts = (pts) => `${Number(pts).toLocaleString("pt-BR")} pts`;

    const cardInfo = order.card?.brand
      ? `${order.card.brand} - final ${order.card.last_digits || order.card.lastFourDigits || "****"}`
      : "";

    const isPixPayment = order.payment?.type === "PIX";
    const orderStatus = (order.status || "").toLowerCase();

    if (isPixPayment) {
      let description = "";
      let pixData = null;

      if (orderStatus === "pendente") {
        description = `${formatBRL(priceBRL)}, aguardando pagamento`;
        pixData = {
          pixCode: order.payment.pixCode,
          pixQrCode: order.payment.pixQrCode,
          pixExpiresAt: order.payment.pixExpiresAt,
        };
      } else if (orderStatus === "aprovado") {
        description = `${formatBRL(priceBRL)}, à vista.`;
      } else {
        description = `${formatBRL(priceBRL)}. Prazo para pagamento expirado.`;
      }

      if (payPoints > 0) {
        description += ` + ${formatPts(payPoints)}`;
      }

      return { type: "PIX", description, cardInfo: "", pixData };
    }

    if (paymentType === "both") {
      return {
        type: "Cartão e Pontos",
        description: `${formatBRL(priceBRL)} + ${formatPts(payPoints)}`,
        cardInfo,
      };
    }

    if (
      paymentType === "card" ||
      paymentType === "cards" ||
      paymentType === "money"
    ) {
      const installments = order.installments;
      let installmentText = "";
      if (installments && Number(installments.installmentNumber) > 0) {
        installmentText =
          installments.installmentNumber === 1
            ? "à vista"
            : `em ${installments.installmentNumber}x sem juros`;
      }
      const base = formatBRL(
        priceBRL > 0
          ? priceBRL
          : parseFloat(order.pricing?.total || order.totalOrder || 0),
      );
      return {
        type: "Cartão de Crédito",
        description: installmentText ? `${base} ${installmentText}` : base,
        cardInfo,
      };
    }

    if (paymentType === "points" || paymentType === "pontos") {
      const points =
        payPoints > 0
          ? payPoints
          : parseFloat(order.pricing?.total || order.subtotal || 0);
      return {
        type: "Pontos",
        description: formatPts(points),
        cardInfo: "",
      };
    }

    return {
      type: "Aguardando pagamento",
      description: "",
      cardInfo: "",
    };
  };

  const mapShippingAddress = (order) => {
    const address = order.shippingAddress;
    if (!address || !address.streetName) return null;

    return {
      label: address.label || "Casa",
      street: `${address.streetName}, ${address.number || "s/n"}`,
      neighborhood: address.neighborhood,
      complement: address.complement,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
    };
  };

  const getTotalOrder = (order) => {
    const paymentType = order.payment_type || order.payment?.type;
    const payPriceCents =
      typeof order.pay_price === "number" ? order.pay_price : 0;
    const payPoints =
      typeof order.pay_points === "number" ? order.pay_points : 0;
    const formatBRL = (value) =>
      `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    if (paymentType === "both") {
      const brl = formatBRL(payPriceCents / 100);
      const pts = `${Number(payPoints).toLocaleString("pt-BR")} pts`;
      return `${brl} + ${pts}`;
    }

    if (paymentType === "card" || paymentType === "cards") {
      const total =
        payPriceCents > 0
          ? payPriceCents / 100
          : parseFloat(order.pricing?.total || order.totalOrder || 0);
      return formatBRL(total);
    }

    if (paymentType === "points" || paymentType === "pontos") {
      const points =
        payPoints > 0
          ? payPoints
          : parseFloat(order.pricing?.total || order.subtotal || 0);
      return `${Number(points).toLocaleString("pt-BR")} pts`;
    }

    if (order.pricing?.total) {
      return formatBRL(parseFloat(order.pricing.total));
    }
    if (order.totalOrder) {
      return formatBRL(parseFloat(order.totalOrder));
    }
    if (order.subtotal) {
      return `${parseFloat(order.subtotal).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pts`;
    }
    return "—";
  };

  const fetchOrders = useCallback(
    async (requestedPage) => {
      setLoading(true);
      try {
        const { startDate, endDate } = getPeriod(period);
        const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
        const { data } = await pedidosApi.get(`/v1/pedidos/${channel}`, {
          params: {
            endDate,
            startDate,
            customerUuid,
            limit,
            page: requestedPage,
          },
        });
        const list = Array.isArray(data?.data) ? data.data : [];
        const mapped = list.map((o) => {
          let orderType = "product";
          if (o.orderType === "hoteis") orderType = "hotel";
          else if (o.orderType === "passagens") orderType = "aereo";
          else if (o.orderType === "points_transfer")
            orderType = "points_transfer";

          const hasCoupon = o.discountPoints > 0 || o.couponCode;
          return {
            orderUuid: o.orderUuid,
            status: o.status,
            orderType: orderType,
            rawData: o,
            productData: orderType === "product" ? mapProductData(o) : null,
            hotelData: orderType === "hotel" ? mapHotelData(o) : null,
            aereoData: orderType === "aereo" ? mapAereoData(o) : null,
            paymentData: mapPaymentData(o),
            shippingAddress:
              orderType === "product" ? mapShippingAddress(o) : null,
            totalOrder: hasCoupon && o.subtotalPoints 
              ? `${Number(o.subtotalPoints).toLocaleString("pt-BR")} pts` 
              : getTotalOrder(o),
            discountPoints: o.discountPoints,
            couponCode: o.couponCode,
          };
        });

        setDataResponse((prev) => {
          const existing = new Set(prev.map((p) => p.orderUuid));
          const newItems = mapped.filter((m) => !existing.has(m.orderUuid));
          const merged = [...prev, ...newItems];

          return merged.sort((a, b) => {
            const dateA = new Date(a.rawData.orderDate);
            const dateB = new Date(b.rawData.orderDate);
            return dateB - dateA;
          });
        });
        setHasMore(mapped.length === limit);
      } catch (e) {
        console.error("Error fetching orders:", e);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [customerUuid, limit, period],
  );

  useEffect(() => {
    if (!customerUuid) return;
    setDataResponse([]);
    setPage(1);
    setHasMore(true);
  }, [customerUuid, period, location.key]);

  useEffect(() => {
    if (customerUuid) fetchOrders(page);
  }, [customerUuid, page, fetchOrders]);

  useEffect(() => {
    if (screenName) {
      sendMyRequestsAnalytics(getPeriodAnalyticsTitle(period));
    }
  }, [screenName]);

  useEffect(() => {
    if (!hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { rootMargin: "200px" },
    );

    const el = loadMoreRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [hasMore, loading]);

  return (
    <>
      {loading && dataResponse.length === 0 ? (
        <LoaderSmall />
      ) : (
        <PageWrap>
        <Container className="wl-page">
          <Title style={{ marginTop: "48px", paddingBottom: "24px" }}>
            Meus Pedidos
          </Title>

          <p
            className="fw-bolder font-wl-700 text-mlg mb-3"
            style={{ color: "var(--primary-color)" }}
          >
            Escolha um período para exibição
          </p>

          <TextField
            id="period-select"
            select
            label="Selecione o período"
            className="w-100 mb-4"
            variant="standard"
            value={period}
            onChange={handleChangeRange}
            style={{
              color: "var(--primary-color)",
              borderBottom: "1px solid var(--primary-color)",
            }}
          >
            {periods.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <AccordionRequest>
            {dataResponse?.map((order, index) => (
              <Accordion
                sx={{ boxShadow: 0 }}
                className="accordion"
                key={index}
                onChange={handleAccordionChange(index, order)}
              >
                <AccordionSummary
                  className="accordion-summary p-0"
                  expandIcon={<HiOutlineChevronDown />}
                  aria-controls="panel1a-content"
                >
                  <Typography
                    className="title"
                    style={{
                      color: "var(--primary-color) !important",
                      fontWeight: "500",
                    }}
                  >
                    {formatOrderDate(order?.rawData)} - Pedido{" "}
                    {getFirstNumbersOrder(order.orderUuid)} -{" "}
                    <span
                      style={{
                        color:
                          order.status?.toLowerCase() === "pendente" &&
                          order.rawData?.payment?.type === "PIX"
                            ? "#D43F11"
                            : order.status?.toLowerCase() === "aprovado"
                              ? "#00BFA6"
                              : order.status?.toLowerCase() === "cancelado"
                                ? "#f90202"
                                : order.status?.toLowerCase() === "negado"
                                  ? "#d23c74"
                                  : "#F5A623",
                        fontWeight: "500",
                      }}
                    >
                      {order.status?.toLowerCase() === "pendente" &&
                      order.rawData?.payment?.type === "PIX"
                        ? "Aguardando pagamento"
                        : order.status}
                    </span>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ProductHeader
                    showDeliveryAddress={
                      order?.orderType === "product" && !!order?.shippingAddress
                    }
                    paymentData={order?.paymentData}
                    shippingAddress={order?.shippingAddress}
                    totalOrder={order?.totalOrder}
                    status={
                      order?.status?.toLowerCase() === "pendente" &&
                      order?.rawData?.payment?.type === "PIX"
                        ? "Aguardando pagamento"
                        : order?.status
                    }
                    discountPoints={order?.discountPoints}
                    couponCode={order?.couponCode}
                  />
                  <Content>
                    {order?.orderType === "product" &&
                      order?.productData &&
                      order.productData.map((product, idx) => (
                        <ProductCard
                          key={idx}
                          product={product}
                          hideTracking={
                            order?.status?.toLowerCase() === "pendente" &&
                            order?.rawData?.payment?.type === "PIX"
                          }
                        />
                      ))}
                    {order?.orderType === "hotel" && order?.hotelData && (
                      <ProductHotel reservation={order.hotelData} />
                    )}
                    {order?.orderType === "aereo" && order?.aereoData && (
                      <ProductAereo itinerary={order.aereoData} />
                    )}
                    {order?.orderType === "points_transfer" && (
                      <ProductTransfer />
                    )}
                  </Content>
                </AccordionDetails>
                <div>
                  <Title
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      marginBottom: "16px",
                    }}
                  >
                    Precisa de ajuda com seu pedido? Fale com a gente!
                  </Title>
                  <div style={{ maxWidth: "100%", wordBreak: "break-word" }}>
                    <Bold
                      style={{
                        fontSize: "12px",
                        marginBottom: "12px",
                        marginTop: "0px",
                      }}
                    >
                      Central de atendimento:
                    </Bold>
                    <Bold
                      style={{
                        fontSize: "13px",
                        fontWeight: "300",
                        marginBottom: "12px",
                        marginTop: "0px",
                      }}
                    >
                      Capitais e regiões metropolitanas: 4001-4460
                    </Bold>
                    <Bold
                      style={{
                        fontSize: "13px",
                        fontWeight: "300",
                        marginBottom: "12px",
                        marginTop: "0px",
                      }}
                    >
                      Demais localidades: 0800 728 4460
                    </Bold>
                  </div>
                </div>
              </Accordion>
            ))}
          </AccordionRequest>
          <div ref={loadMoreRef} />
          {loading && <div style={{ padding: 16 }}>Carregando...</div>}
        </Container>
        </PageWrap>
      )}
    </>
  );
}
